import { Color } from '@persona-id/design-tokens';
import { uniqBy } from 'lodash/fp';

import {
  collapsedGroupIdToG6Id,
  collapsedGroupToG6CollapsedGroup,
  collapsedPairwiseEdgeToG6EdgeWithState,
  collapsedPairwiseNodeToG6CollapsedPairwiseNode,
  collapsedPairwiseNodeToG6Node,
  createG6RemoveCollapsedGroupMessage,
  createG6RemoveNodeMessage,
  edgeToG6Edge,
  g6EdgeId,
  g6IdToHashableNodeId,
  g6IdToNodeId,
  nodeIdToG6Id,
  nodeToG6Node,
  setG6NodePosition,
  styleG6Edge,
  transactionEdgeToG6Edge,
} from 'dashboard/components/GraphCanvasV2/G6Mapper';
import G6Wrapper, { NodePosition } from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import { defaultNodeState } from 'dashboard/lib/stubs/graph';
import {
  CanvasSelectable,
  CollapsedGroup,
  CollapsedGroupId,
  collapsedGroupIdToSelectable,
  CollapsedGroupPreviewData,
  CollapsedPairwiseNode,
  CollapsedPairwiseNodeId,
  collapsedPairwiseNodeIdToSelectable,
  ColorRule,
  ColorRuleCondition,
  ColorRuleStatus,
  ColorRuleTag,
  combineGraphUpdateMessages,
  CustomDatumTypeRenderConfig,
  DatumType,
  DatumTypeToken,
  Directionality,
  Edge,
  extractSelectedCollapsedGroupIds,
  extractSelectedCollapsedPairwiseNodeIds,
  extractSelectedNodeIds,
  G6AddNodeMessage,
  G6CollapsedGroup,
  G6Edge,
  G6GraphUpdateMessage,
  G6NodeId,
  G6SetGraphMessage,
  HashableCanvasSelectable,
  HashableNodeId,
  HashableNodeIdPair,
  hashableNodeIdPairToHashable,
  HoverCardState,
  newEmptyGraphUpdateMessage,
  Node,
  NodeConditions,
  NodeId,
  nodeIdFromHashable,
  nodeIdPairToHashable,
  nodeIdToHashable,
  nodeIdToSelectable,
  NodeRenderConfig,
  NodeState,
  PropertyConfig,
  RenderedCanvasNodeType,
  selectableFromHashable,
  selectableToHashable,
  V2Data,
} from 'dashboard/models/graph';
import {
  HydratedNode,
  LightlyHydratedNode,
  NodeIdAndLightHydration,
  ServerLightlyHydratedAccount,
} from 'dashboard/models/graph-hydration';
import { SegmentClient } from 'lib/clients/SegmentClient';
import {
  ACCOUNT_DATUM_TYPE,
  ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE,
  ADDRESS_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  COLLAPSED_PAIRWISE_NODE_TYPE_RENDER_CONFIG,
  DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  EXTERNAL_ACCOUNT_DATUM_TYPE,
  EXTERNAL_ACCOUNT_DATUM_TYPE_RENDER_CONFIG,
  GOVERNMENT_ID_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PERSONA_DATUM_TYPE_TO_DATUM_TYPE,
  PERSONA_DATUM_TYPES,
  PHONE_NUMBER_DATUM_TYPE,
  TRANSACTIONS_DATUM_TYPE,
  TRANSACTIONS_DATUM_TYPE_RENDER_CONFIG,
  UNKNOWN_DATUM_TYPE_RENDER_CONFIG,
} from 'lib/constants/graph';
import { assertNeverAndThrow } from 'lib/utils/types';

const reverse = (directionality: Directionality) => {
  switch (directionality) {
    case Directionality.Forward:
      return Directionality.Backward;
    case Directionality.Backward:
      return Directionality.Forward;
    case Directionality.Bidirectional:
      return Directionality.Bidirectional;
  }
};

const normalizeNodeIdPair = (nodeId1: NodeId, nodeId2: NodeId): [NodeId, NodeId] => {
  const [sortedNodeId1, sortedNodeId2] = [nodeId1, nodeId2].sort();
  return [sortedNodeId1, sortedNodeId2];
};

const normalizeEdge = (edge: Edge): Edge => {
  const nodeIds = [edge.id1, edge.id2];
  nodeIds.sort();
  const [id1, id2] = nodeIds;
  const directionality = edge.id1 === id1 ? edge.directionality : reverse(edge.directionality);
  return {
    id1,
    id2,
    directionality,
  };
};

const zoomSteps = [2, 3, 6, 13, 25, 50, 100, 150, 200];
const zoomStepsReversed = zoomSteps.map((x) => x).reverse();
const zoomOutFrom = (val: number) => {
  return zoomStepsReversed.find((step) => step < val);
};
const zoomInFrom = (val: number) => {
  return zoomSteps.find((step) => step > val);
};

const emptyHoverCard = (): HoverCardState => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return null;
};

export enum GraphViewTypes {
  Parsed = 'Parsed',
  Expanded = 'Expanded',
}

export type AccountNodeColorlookupFn = (
  hydrationData: ServerLightlyHydratedAccount,
  isFeaturedNode: boolean,
) => string;
export type CustomDatumTypeRenderConfigLookupFn = (
  token: DatumTypeToken,
) => CustomDatumTypeRenderConfig;
export type CanvasUpdateFn = (updateMessage: G6GraphUpdateMessage) => void;
export type DatumTypeLookupFn = (token: DatumTypeToken) => DatumType;
export type NodeRenderConfigLookupFn = (token: DatumTypeToken) => NodeRenderConfig;
export type CanvasSelectableChangeHandlerFn = (selectables: CanvasSelectable[]) => void;
export type StateMirrorRefreshFn = () => void;

export interface AddNodeArguments {
  node: Node;
  nonfetchedEdgeCount: number;
  lightlyHydratedNode: LightlyHydratedNode;
  position?: NodePosition;
}

export interface Clique {
  id: string;
  entityNodes: Set<HashableNodeId>;
  propertyNodes: Set<HashableNodeId>;
}

export enum GraphExplorerQueryLoadingStage {
  // when sending a new query, the flow is
  // sendingQuery > waitingForGraphQueryResult > HydratingGraphNodes > RenderingQueryResult > Completed
  // when loading the graph query in the query-id url param, the flow is
  // loadingGraph > fetchingQuery > HydratingGraphNodes > RenderingQueryResult > Completed
  SendingQuery = 'sendingQuery',
  WaitingForGraphQueryResult = 'waitingForGraphQueryResult',
  LoadingGraph = 'loadingGraph',
  FetchingQuery = 'fetchingQuery',
  HydratingGraphNodes = 'hydratingGraphNodes',
  RenderingQueryResult = 'renderingQueryResult',
  Completed = 'completedQuery',
  Idle = 'idle',
}

export interface GraphExplorerQueryLoadingState {
  stage: GraphExplorerQueryLoadingStage;
  setTime: Date;
}

// Tracks all of the state necessary to decide what to render on the canvas.
// Functions that will require a canvas update will return a G6GraphUpdateMessage, which should be sent directly
// to the G6Wrapper so that the canvas state matches the state.
export class GraphExplorer {
  // TODO(kerry): Store the graph token here, once Graph configuration is multigraph-aware.
  // TODO(kerry): Store the query corresponding to the data here as well.

  private nodeDatumTypesByToken: Map<DatumTypeToken, DatumType>;
  private coloringRules: ColorRule[];
  private nodeRenderConfigsByToken: Map<DatumTypeToken, NodeRenderConfig>;
  private propertyConfigs: PropertyConfig[];

  // --------------------------------------------------------------------------
  // State for tracking connectivity information and hydration data.
  // --------------------------------------------------------------------------
  private nodes: Map<HashableNodeId, Node>;
  private edges: Map<HashableNodeIdPair, Edge>;
  // Look up node connections, by storing the connectivity information in a mapping from
  // node IDs to a set of connected node IDs.
  private adjacencyLists: Map<HashableNodeId, Set<HashableNodeId>>;
  private nonfetchedEdgeCounts: Map<HashableNodeId, number>;

  // Quick lookup for selected nodes.
  private selected: Set<HashableCanvasSelectable>;
  private onSelectablesChangeHandlers: Map<string, CanvasSelectableChangeHandlerFn> = new Map();

  private highlightedNodes: Set<HashableNodeId>;
  private newNodes: Set<HashableNodeId>;
  private seedNodes: Set<HashableNodeId>;
  private excludedNodes: Set<HashableNodeId>;
  private highlightedCollapsedGroups: Set<CollapsedGroupId>;
  private highlightedCollapsedPairwiseNodes: Set<CollapsedPairwiseNodeId>;

  // Hidden nodes should always be nodes that are not reached by BFS from
  // seed nodes after skipping excluded nodes
  private hiddenNodes: Set<HashableNodeId>;
  // Visible nodes should always be `this.nodes - this.hiddenNodes`
  private visibleNodes: Set<HashableNodeId>;

  // groups of nodes collapsed into a single node
  private collapsedGroups: Map<CollapsedGroupId, CollapsedGroup>;
  // groups of pairwise nodes collapsed into a single node
  private collapsedPairwiseNodes: Map<CollapsedPairwiseNodeId, CollapsedPairwiseNode>;
  // quick lookup of which collapsedGroup a Node is in.
  private nodeIdToCollapsedGroup: Map<HashableNodeId, CollapsedGroupId>;
  // quick lookup of which collapsedGroup a Node is in.
  private nodeIdToCollapsedPairwiseNode: Map<HashableNodeId, CollapsedPairwiseNodeId>;
  // number for generating new collapsed group ids
  private collapsedGroupNonce: number;
  // number for generating new collapsed pairwise node ids
  private collapsedPairwiseNodeNonce: number;

  // --------------------------------------------------------------------------
  // Canvas-related callback.
  // --------------------------------------------------------------------------
  private canvasUpdateFn: CanvasUpdateFn;

  // Zoom percentage stored as a number > 0. eg. 100, 125, 150.
  private zoomPercentage;
  private hoverCardState: HoverCardState;
  public excludedNodesVisible: boolean;
  public selectedViewType: string;
  private viewTypeSwitchEnabled: boolean;

  private cliques: Map<CollapsedPairwiseNodeId, Clique>;
  private expandedCliqueIds: Set<CollapsedPairwiseNodeId>;
  private collapsedCliqueIds: Set<CollapsedPairwiseNodeId>;

  private parsedNodesIdToNodes: Map<HashableNodeId, Node>;

  private lightHydrationData: Map<HashableNodeId, LightlyHydratedNode>;
  private hydrationData: Map<HashableNodeId, HydratedNode>;

  // --------------------------------------------------------------------------
  // Property filters, so that we can know how to render new nodes in
  // accordance to the existing property filters.
  // --------------------------------------------------------------------------
  public selectedProperties: Set<DatumTypeToken>;
  private dimmedProperties: Set<DatumTypeToken>;

  public reactSubscribers: Set<() => void> = new Set();

  // TODO(jjowdy): There's some duplication/overwriting happening here.
  constructor(viewTypeSwitchEnabled: boolean, viewType: string = GraphViewTypes.Parsed) {
    this.nodeDatumTypesByToken = new Map();
    this.nodeRenderConfigsByToken = new Map();
    this.propertyConfigs = [];
    this.coloringRules = [];
    this.nodes = new Map();
    this.edges = new Map();
    this.adjacencyLists = new Map();
    this.coloringRules = [];
    this.hoverCardState = emptyHoverCard();
    this.selectedProperties = new Set();
    PERSONA_DATUM_TYPES.forEach((datumType: DatumType) => {
      this.selectedProperties.add(datumType.token);
    });
    this.dimmedProperties = new Set();
    this.canvasUpdateFn = () => undefined;
    this.edges = new Map();
    this.highlightedNodes = new Set();
    this.hydrationData = new Map();
    this.lightHydrationData = new Map();
    this.newNodes = new Set();
    this.nodes = new Map();
    this.nonfetchedEdgeCounts = new Map();
    this.selected = new Set();
    this.zoomPercentage = 100;
    this.seedNodes = new Set();
    this.excludedNodes = new Set();
    this.hiddenNodes = new Set();
    this.visibleNodes = new Set();
    this.excludedNodesVisible = false;
    this.collapsedGroupNonce = 0;
    this.collapsedPairwiseNodeNonce = 0;
    this.collapsedGroups = new Map();
    this.collapsedPairwiseNodes = new Map();
    this.nodeIdToCollapsedGroup = new Map();
    this.highlightedCollapsedGroups = new Set();
    this.highlightedCollapsedPairwiseNodes = new Set();
    this.selectedViewType = viewType;
    this.viewTypeSwitchEnabled = viewTypeSwitchEnabled;
    this.expandedCliqueIds = new Set();
    this.collapsedCliqueIds = new Set();
    this.cliques = new Map();
    this.parsedNodesIdToNodes = new Map();
    this.nodeIdToCollapsedPairwiseNode = new Map();
  }

  clear() {
    this.adjacencyLists.clear();
    this.edges.clear();
    this.highlightedNodes.clear();
    this.hydrationData.clear();
    this.lightHydrationData.clear();
    this.newNodes.clear();
    this.nodes.clear();
    this.selected.clear();
    this.collapsedGroups.clear();
    this.collapsedPairwiseNodes.clear();
    this.nodeIdToCollapsedGroup.clear();
    this.zoomPercentage = 100;
    this.highlightedCollapsedGroups.clear();
    this.highlightedCollapsedPairwiseNodes.clear();
    this.expandedCliqueIds.clear();
    this.collapsedCliqueIds.clear();
    this.cliques.clear();
    this.parsedNodesIdToNodes.clear();
    this.nodeIdToCollapsedPairwiseNode.clear();

    // Reset the selected properties to all known properties.
    this.selectedProperties.clear();
    PERSONA_DATUM_TYPES.forEach((datumType: DatumType) => {
      this.selectedProperties.add(datumType.token);
    });
    Array.from(this.nodeRenderConfigsByToken.keys()).forEach((datumTypeToken) => {
      this.selectedProperties.add(datumTypeToken);
    });

    // By default, no properties are dimmed
    this.dimmedProperties.clear();
  }

  // --------------------------------------------------------------------------
  // Callbacks to be set immediately after initialization.
  // --------------------------------------------------------------------------

  setCanvasUpdateCallback(canvasUpdateFn: CanvasUpdateFn) {
    this.canvasUpdateFn = canvasUpdateFn;
  }

  // --------------------------------------------------------------------------
  // Data setting functions.
  // --------------------------------------------------------------------------

  setData(
    data: V2Data,
    lightHydrationData: Map<HashableNodeId, LightlyHydratedNode>,
    nodePositions: { [nodeId: HashableNodeId]: NodePosition } = {},
  ): G6SetGraphMessage {
    this.clear();

    this.lightHydrationData = lightHydrationData;

    const message: G6SetGraphMessage = {
      nodes: [],
      edges: [],
    };

    // Add the raw shape of the graph.
    (data?.nodes || []).forEach((node) => {
      const nonfetchedEdgeCount = data.nonfetchedEdgeCounts.get(nodeIdToHashable(node.id)) ?? 0;
      const hashableNodeId = nodeIdToHashable(node.id);
      const lightlyHydratedNode = lightHydrationData.get(hashableNodeId);
      const addNodeMessage = this.addNodeWithoutCanvasUpdate(
        node,
        nonfetchedEdgeCount,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lightlyHydratedNode,
        nodePositions[hashableNodeId],
      );

      message.nodes.push((addNodeMessage.items[0] as G6AddNodeMessage).node);
    });

    (data?.edges || []).forEach((edge) => {
      this.addEdgeWithoutCanvasUpdate(edge);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      message.edges.push(this.normalEdgeOrTxnEdgeToG6Edge(edge, lightHydrationData));
    });

    if (this.viewTypeSwitchEnabled) this.getCliques();

    if (this.selectedViewType === GraphViewTypes.Parsed) {
      this.collapsedCliqueIds.forEach((cliqueId) => {
        const clique = this.cliques.get(cliqueId);

        // remove property nodes that are in the cliques from the message
        message.nodes = message.nodes.filter((node) => {
          const hashableNodeId = g6IdToHashableNodeId(node.id);
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          const isParsedPropertyNode = clique.propertyNodes.has(hashableNodeId);

          if (isParsedPropertyNode) {
            // store the removed nodes for when users toggles the expand view
            this.parsedNodesIdToNodes.set(hashableNodeId, node.dataNode);
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.nodeIdToCollapsedPairwiseNode.set(hashableNodeId, clique.id);
          }
          return !isParsedPropertyNode;
        });

        // adding the collapsed pairwise nodes to the canvas
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const newCollapsedNode = this.createCollapsedPairwiseNode(clique);
        message.nodes.push(collapsedPairwiseNodeToG6Node(newCollapsedNode));

        // add edges from the collapsed node to entity nodes of the clique
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        clique.entityNodes.forEach((node) => {
          const newEdge = this.createCollapsedPairwiseEdge(node, newCollapsedNode);
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          message.edges.push(this.collapsedPairwiseEdgeToG6Edge(newEdge));
        });
      });
    }

    this.reactBroadcast();
    return message;
  }

  private newUpdateNodeMessage(node: Node): G6GraphUpdateMessage {
    return this.newUpdateNodesMessage([node]);
  }

  private newUpdateNodesMessage(nodes: Node[]): G6GraphUpdateMessage {
    // do not update a node that is contained in a collapsed group
    return {
      items: nodes
        .filter((node) => !this.nodeIdToCollapsedGroup.has(nodeIdToHashable(node.id)))
        .map((node) => ({
          messageType: 'update_node',
          node: nodeToG6Node(
            node,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.lightHydrationData.get(nodeIdToHashable(node.id)),
            this.getNodeRenderConfig,
            this.getAccountNodeColor,
          ),
        })),
    };
  }

  private newAddEdgesMessage(edges: Edge[]): G6GraphUpdateMessage {
    const g6Edges = edges.map((e) => {
      return edgeToG6Edge(
        e,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(nodeIdToHashable(e.id1)).state,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(nodeIdToHashable(e.id2)).state,
      );
    });

    return {
      items: this.retargetG6Edges(g6Edges).map((g6Edge) => {
        return {
          messageType: 'add_edge',
          edge: g6Edge,
        };
      }),
    };
  }

  private newAddCollapsedPairwiseEdgesMessage(edges: Edge[]): G6GraphUpdateMessage {
    const g6Edges = edges.map((e) => {
      return collapsedPairwiseEdgeToG6EdgeWithState(
        e,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(nodeIdToHashable(e.id1)).state,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.collapsedPairwiseNodes.get(e.id2.value).state,
      );
    });

    return {
      items: this.retargetG6Edges(g6Edges).map((g6Edge) => {
        return {
          messageType: 'add_edge',
          edge: g6Edge,
        };
      }),
    };
  }

  // when an edge is to be updated, update the corresponding g6Edge instead.
  private newUpdateEdgesMessage(edges: Edge[]): G6GraphUpdateMessage {
    const g6Edges = edges.map((e) => {
      return edgeToG6Edge(
        e,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(nodeIdToHashable(e.id1)).state,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(nodeIdToHashable(e.id2)).state,
      );
    });

    return {
      items: this.retargetG6Edges(g6Edges).map((g6Edge) => {
        return {
          messageType: 'update_edge',
          edge: g6Edge,
        };
      }),
    };
  }

  private newUpdateEdgeMessage(edge: Edge): G6GraphUpdateMessage {
    return this.newUpdateEdgesMessage([edge]);
  }

  private newUpdateCollapsedGroupMessage(collapsedGroup: CollapsedGroup): G6GraphUpdateMessage {
    return this.newUpdateCollapsedGroupsMessage([collapsedGroup]);
  }

  private newUpdateCollapsedGroupsMessage(collapsedGroups: CollapsedGroup[]): G6GraphUpdateMessage {
    return {
      items: collapsedGroups.map((collapsedGroup) => ({
        messageType: 'update_collapsed_group',
        collapsedGroup: collapsedGroupToG6CollapsedGroup(collapsedGroup),
      })),
    };
  }

  private newUpdateCollapsedPairwiseNodeMessage(
    collapsedNode: CollapsedPairwiseNode,
  ): G6GraphUpdateMessage {
    return this.newUpdateCollapsedPairwiseNodesMessage([collapsedNode]);
  }

  private newUpdateCollapsedPairwiseNodesMessage(
    collapsedNodes: CollapsedPairwiseNode[],
  ): G6GraphUpdateMessage {
    return {
      items: collapsedNodes.map((collapsedNode) => ({
        messageType: 'update_collapsed_pairwise_node',
        node: collapsedPairwiseNodeToG6CollapsedPairwiseNode(collapsedNode),
      })),
    };
  }

  private newAddCollapsedPairwiseNodesMessage(
    collapsedNodes: CollapsedPairwiseNode[],
  ): G6GraphUpdateMessage {
    return {
      items: collapsedNodes.map((collapsedNode) => ({
        messageType: 'add_collapsed_pairwise_node',
        node: collapsedPairwiseNodeToG6CollapsedPairwiseNode(collapsedNode),
      })),
    };
  }

  private newRemoveCollapsedPairwiseNodesMessage(
    collapsedNodes: CollapsedPairwiseNode[],
  ): G6GraphUpdateMessage {
    return {
      items: collapsedNodes.map((collapsedNode) => ({
        messageType: 'remove_collapsed_pairwise_node',
        node: collapsedPairwiseNodeToG6CollapsedPairwiseNode(collapsedNode),
      })),
    };
  }

  getNodesByDatumType = (datumType: DatumTypeToken): NodeId[] => {
    const visibleNodes: NodeId[] = [];
    Array.from(this.nodes.keys()).forEach((hashableNodeId) => {
      const nodeId = nodeIdFromHashable(hashableNodeId as HashableNodeId);
      if (nodeId.token === datumType) {
        visibleNodes.push(nodeId);
      }
    });
    return visibleNodes;
  };

  public getAllNodes = (): Node[] => Array.from(this.nodes.values());

  public getNodeById(hashableNodeId: HashableNodeId): Node {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.nodes.get(hashableNodeId);
  }

  public getCollapsedGroupById(collapsedGroupId: CollapsedGroupId): CollapsedGroup {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.collapsedGroups.get(collapsedGroupId);
  }

  public getCollapsedPairwiseNodeById(
    collapsedPairwiseNodeId: CollapsedPairwiseNodeId,
  ): CollapsedPairwiseNode {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.collapsedPairwiseNodes.get(collapsedPairwiseNodeId);
  }

  public getEdgeByNodeIds(sourceId: HashableNodeId, targetId: HashableNodeId): Edge {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.edges.get(hashableNodeIdPairToHashable(sourceId, targetId));
  }

  public getAllEdges = (): Edge[] => Array.from(this.edges.values());

  // ---------------------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------------------
  private isDisconnectedNode(
    hashableNodeId: HashableNodeId,
    nodesToFilterOut: Set<HashableNodeId>,
  ): boolean {
    let isDisconnectedNode = true;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId).forEach((neighborHashableNodeId) => {
      // If any neighbor nodes are visible, then it's not disconnected
      if (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(neighborHashableNodeId).state.visible === true &&
        !nodesToFilterOut.has(neighborHashableNodeId)
      ) {
        isDisconnectedNode = false;
      }
    });
    return isDisconnectedNode;
  }

  getPropertyFilters = (): DatumTypeToken[] => {
    return Array.from(this.selectedProperties.keys());
  };

  setPropertyFilters(includedDatumTypes: DatumTypeToken[]) {
    this.selectedProperties.clear();
    includedDatumTypes.forEach((datumTypeToken) => {
      this.selectedProperties.add(datumTypeToken);
    });

    const nodesToFilterOut = new Set<HashableNodeId>();
    const message: G6GraphUpdateMessage = newEmptyGraphUpdateMessage();

    // Apply the filter to all nodes
    const nodeIdsToVisit = Array.from(this.nodes.keys());

    // Filter out all property nodes that aren't in given property types
    nodeIdsToVisit.forEach((hashableNodeId) => {
      const nodeId = nodeIdFromHashable(hashableNodeId as HashableNodeId);
      if (
        nodeId.token !== ACCOUNT_DATUM_TYPE.token &&
        !includedDatumTypes.includes(nodeId.token as DatumTypeToken) &&
        // HAX: We should handle filters on custom datum types properly, rather than having them always shown.
        PERSONA_DATUM_TYPES.map((personaDatumType) => personaDatumType.token).includes(nodeId.token)
      ) {
        nodesToFilterOut.add(hashableNodeId as HashableNodeId);
      }
    });

    // Set appropriate visibility state
    Array.from(this.nodes.keys()).forEach((hashableNodeId) => {
      if (nodesToFilterOut.has(hashableNodeId as HashableNodeId)) {
        this.hideNode(nodeIdFromHashable(hashableNodeId as HashableNodeId));
      } else {
        this.showNode(nodeIdFromHashable(hashableNodeId as HashableNodeId));
      }
    });

    // Clean up any disconnected nodes in a second pass
    nodeIdsToVisit
      .filter((hashableNodeId) => this.isDisconnectedNode(hashableNodeId, nodesToFilterOut))
      .forEach((disconnectedNode) => {
        this.hideNode(nodeIdFromHashable(disconnectedNode as HashableNodeId));
      });

    // Update the canvas accordingly.
    this.canvasUpdateFn(message);
  }

  // --------------------------------------------------------------------------
  // Export data functions.
  // --------------------------------------------------------------------------
  getAdjacentPropertyNodes = (accountNodeId: NodeId): HashableNodeId[] => {
    const datumTypesToHydrateForExport = [
      ADDRESS_DATUM_TYPE.token,
      BIRTHDATE_DATUM_TYPE.token,
      BROWSER_FINGERPRINT_DATUM_TYPE.token,
      DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
      DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token,
      EMAIL_DATUM_TYPE.token,
      GOVERNMENT_ID_DATUM_TYPE.token,
      NAME_DATUM_TYPE.token,
      NETWORK_DATUM_TYPE.token,
      PHONE_NUMBER_DATUM_TYPE.token,
    ];
    const neighborsToHydrate = [];
    const neighbors = this.adjacencyLists.get(nodeIdToHashable(accountNodeId));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    for (const neighbor of neighbors) {
      const token = nodeIdFromHashable(neighbor).token;
      if (datumTypesToHydrateForExport.includes(token) || this.nodeDatumTypesByToken.has(token)) {
        neighborsToHydrate.push(neighbor);
      }
    }
    return neighborsToHydrate;
  };

  getAdjacentAccountNodes = (propertyNodeId: NodeId): HashableNodeId[] => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return [...this.adjacencyLists.get(nodeIdToHashable(propertyNodeId))].filter(
      (neighbour) => nodeIdFromHashable(neighbour).token === ACCOUNT_DATUM_TYPE.token,
    );
  };

  // ---------------------------------------------------------------------------------------
  // Adding/removing nodes and/or edges.
  // ---------------------------------------------------------------------------------------

  clearNewNodesStatus = (): G6GraphUpdateMessage[] => {
    const newNodeMessages: G6GraphUpdateMessage[] = [];

    this.newNodes.forEach((hashableNodeId) => {
      const node = this.getNodeById(hashableNodeId);
      node.state.new = false;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.adjacencyLists.get(hashableNodeId).forEach((neighborHashableNodeId) => {
        const neighborNodeId = nodeIdFromHashable(neighborHashableNodeId);
        const edge = this.edges.get(nodeIdPairToHashable(node.id, neighborNodeId));

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        newNodeMessages.push(this.newUpdateEdgeMessage(edge));
      });
    });
    this.newNodes.clear();
    return newNodeMessages;
  };

  addNode({ node, nonfetchedEdgeCount, lightlyHydratedNode, position }: AddNodeArguments) {
    const message = this.addNodeWithoutCanvasUpdate(
      node,
      nonfetchedEdgeCount,
      lightlyHydratedNode,
      position,
    );

    // Update the canvas accordingly.
    this.canvasUpdateFn(message);
  }

  bulkAddNodesWithoutCanvasUpdate(nodeArguments: AddNodeArguments[]) {
    const updateNewNodesMessages = this.clearNewNodesStatus();
    const messages = nodeArguments.map(
      ({ node, nonfetchedEdgeCount, lightlyHydratedNode, position }: AddNodeArguments) =>
        this.addNodeWithoutCanvasUpdate(node, nonfetchedEdgeCount, lightlyHydratedNode, position),
    );

    return messages.concat(updateNewNodesMessages);
  }

  private updateNodeWithoutCanvasUpdate(
    node: Node,
    nonfetchedEdgeCount: number,
    lightlyHydratedNode: LightlyHydratedNode,
    position?: NodePosition,
  ): G6GraphUpdateMessage {
    const hashableNodeId = nodeIdToHashable(node.id);

    // recheck nonfetchedEdgeCount, visibility, and dimmed
    // Make sure the expansion state is consistent.
    node.state.nonfetchedEdgeCount = nonfetchedEdgeCount;

    // Make sure the property filter state is consistent.
    if (node.id.token !== ACCOUNT_DATUM_TYPE.token) {
      node.state.visible = this.isPropertySelected(node.id.token);
    }

    // if not already dimmed, dim node if property is dimmed
    if (!node.state.dimmed) {
      node.state.dimmed = this.isDatumTypeDimmed(node.id.token);
    }

    // Update the internal state.
    this.nonfetchedEdgeCounts.set(hashableNodeId, nonfetchedEdgeCount);

    // update light hydration data
    this.lightHydrationData.set(hashableNodeId, lightlyHydratedNode);

    const g6Node = nodeToG6Node(
      node,
      lightlyHydratedNode,
      this.getNodeRenderConfig,
      this.getAccountNodeColor,
    );

    if (position != null) {
      setG6NodePosition(g6Node, position);
    }

    const message: G6GraphUpdateMessage = {
      items: [
        {
          messageType: 'update_node',
          node: g6Node,
        },
      ],
    };
    return message;
  }

  private addNodeWithoutCanvasUpdate(
    node: Node,
    nonfetchedEdgeCount: number,
    lightlyHydratedNode: LightlyHydratedNode,
    position?: NodePosition,
  ): G6GraphUpdateMessage {
    if (this.isEmptyAddNodeMessage(node.id)) {
      return newEmptyGraphUpdateMessage();
    }

    const hashableNodeId = nodeIdToHashable(node.id);

    // Make sure the expansion state is consistent.
    node.state.nonfetchedEdgeCount = nonfetchedEdgeCount;

    // Make sure the property filter state is consistent.
    if (node.id.token !== ACCOUNT_DATUM_TYPE.token) {
      node.state.visible = this.isPropertySelected(node.id.token);
    }

    // if not already dimmed, dim node if property is dimmed
    if (!node.state.dimmed) {
      node.state.dimmed = this.isDatumTypeDimmed(node.id.token);
    }

    // Update the internal state.
    this.nodes.set(hashableNodeId, node);
    this.adjacencyLists.set(hashableNodeId, new Set());
    this.nonfetchedEdgeCounts.set(hashableNodeId, nonfetchedEdgeCount);
    this.newNodes.add(hashableNodeId);

    // Remember the lightly hydrated node data.
    this.lightHydrationData.set(hashableNodeId, lightlyHydratedNode);

    const g6Node = nodeToG6Node(
      node,
      lightlyHydratedNode,
      this.getNodeRenderConfig,
      this.getAccountNodeColor,
    );

    if (position != null) {
      setG6NodePosition(g6Node, position);
    }

    const message: G6GraphUpdateMessage = {
      items: [
        {
          messageType: 'add_node',
          node: g6Node,
        },
      ],
    };
    return message;
  }

  private removeNodeWithoutCanvasUpdate(nodeId: NodeId): G6GraphUpdateMessage {
    const hashableNodeId = nodeIdToHashable(nodeId);

    const message: G6GraphUpdateMessage = newEmptyGraphUpdateMessage();

    // Remove all edges connected to the node.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const neighborHashableNodeIds = Array.from(this.adjacencyLists.get(hashableNodeId));
    neighborHashableNodeIds.forEach((neighborHashableNodeId) => {
      const neighborNodeId = nodeIdFromHashable(neighborHashableNodeId);
      this.removeEdge(nodeId, neighborNodeId);
    });

    this.adjacencyLists.delete(hashableNodeId);
    this.nodes.delete(hashableNodeId);
    message.items.push({
      messageType: 'remove_node',
      id: nodeIdToG6Id(nodeId),
    });

    // Update the selected set, if relevant.
    const hashableSelectable = selectableToHashable(nodeIdToSelectable(nodeId));
    this.selected.delete(hashableSelectable);

    return message;
  }

  // Removes the node, as well as all connected edges to the node.
  removeNode(nodeId: NodeId) {
    const message = this.removeNodeWithoutCanvasUpdate(nodeId);

    // Update the canvas accordingly.
    this.canvasUpdateFn(message);
  }

  addEdgesWithoutCanvasUpdate(edges: Edge[]) {
    return edges.map((edge) => this.addEdgeWithoutCanvasUpdate(edge));
  }

  addEdge(edge: Edge) {
    const message = this.addEdgeWithoutCanvasUpdate(edge);

    // Update the canvas accordingly.
    this.canvasUpdateFn(message);
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private addEdgeWithoutCanvasUpdate(edge: Edge): G6GraphUpdateMessage {
    edge = normalizeEdge(edge);
    if (this.containsEdge(edge.id1, edge.id2)) {
      return newEmptyGraphUpdateMessage();
    }
    const hashableNodeId1 = nodeIdToHashable(edge.id1);
    const hashableNodeId2 = nodeIdToHashable(edge.id2);
    const node1 = this.nodes.get(hashableNodeId1);
    const node2 = this.nodes.get(hashableNodeId2);
    if (node1 == null || node2 == null) {
      console.warn(
        `Cannot add edge ${edge.id1} -> ${edge.id2} because one of the nodes is missing`,
      );
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.adjacencyLists.get(hashableNodeId1).add(hashableNodeId2);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.adjacencyLists.get(hashableNodeId2).add(hashableNodeId1);
      this.edges.set(nodeIdPairToHashable(edge.id1, edge.id2), edge);

      const message: G6GraphUpdateMessage = this.newAddEdgesMessage([edge]);
      return message;
    }
  }

  removeEdge(nodeId1: NodeId, nodeId2: NodeId) {
    const [normalizedNodeId1, normalizedNodeId2] = normalizeNodeIdPair(nodeId1, nodeId2);
    [nodeId1, nodeId2] = [normalizedNodeId1, normalizedNodeId2];

    const hashableNodeId1 = nodeIdToHashable(nodeId1);
    const hashableNodeId2 = nodeIdToHashable(nodeId2);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId1).delete(hashableNodeId2);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId2).delete(hashableNodeId1);
    const edge = this.edges.get(nodeIdPairToHashable(nodeId1, nodeId2));
    this.edges.delete(nodeIdPairToHashable(nodeId1, nodeId2));

    const g6Edge = edgeToG6Edge(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      edge,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.nodes.get(hashableNodeId1).state,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.nodes.get(hashableNodeId2).state,
    );
    if (g6Edge) {
      const message: G6GraphUpdateMessage = {
        items: [
          {
            messageType: 'remove_edge',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            edgeId: g6Edge.id,
          },
        ],
      };

      // Update the canvas accordingly.
      this.canvasUpdateFn(message);
    }
  }

  private createCollapsedPairwiseNode(clique: Clique): CollapsedPairwiseNode {
    const collapsedNode: CollapsedPairwiseNode = {
      id: {
        token: ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token,
        value: clique.id,
      },
      members: clique.propertyNodes,
      state: defaultNodeState(),
      hoverCards: new Set(),
    };
    this.collapsedPairwiseNodes.set(collapsedNode.id.value, collapsedNode);
    return collapsedNode;
  }

  private createCollapsedPairwiseEdge(
    entityNode: HashableNodeId,
    collapseNode: CollapsedPairwiseNode,
  ): Edge {
    return {
      id1: nodeIdFromHashable(entityNode),
      id2: collapseNode.id,
      directionality: 'bidirectional',
    } as Edge;
  }

  switchNodesAndEdges(
    targetNodes: Node[],
    targetEdges: Edge[],
    lightHydrationData: Map<HashableNodeId, LightlyHydratedNode>,
    hydrationData: Map<HashableNodeId, HydratedNode>,
    seedNodes: Node[],
    g6Wrapper: G6Wrapper,
  ) {
    const targetNodesSet = new Set(targetNodes.map((node) => nodeIdToHashable(node.id)));

    // nodes that persist should be checked if they are now seed nodes or not
    const persistingNodes = Array.from(this.nodes.keys()).filter((nodeId) =>
      targetNodesSet.has(nodeId),
    );
    const updateNodeMessages = persistingNodes.map((hash) => {
      return this.updateNodeWithoutCanvasUpdate(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(hash),
        0,
        lightHydrationData.get(hash),
      );
    });

    // generate messages to add new nodes
    const newNodes = targetNodes
      .map((node) => {
        return { node, hash: nodeIdToHashable(node.id) };
      })
      .filter(({ hash }) => {
        return !this.nodes.has(hash) && lightHydrationData.has(hash);
      });
    const addNodeMessages = newNodes.map(({ node, hash }) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return this.addNodeWithoutCanvasUpdate(node, 0, lightHydrationData.get(hash));
    });

    // generate messages to remove nodes
    const removeNodeMessages = Array.from(this.nodes.keys())
      .filter((hash) => {
        return !targetNodesSet.has(hash);
      })
      .map((hash) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return this.removeNodeWithoutCanvasUpdate(this.nodes.get(hash).id);
      });

    // after adding the edges and updating the adjacency lists, move the position of new nodes such that
    // they dont start off too far from where they are expected to be. This reduces flickering
    let i = 0;
    const newNodesSet = new Set(newNodes.map(({ hash }) => hash));

    // set position of new nodes
    addNodeMessages.forEach((msg) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      msg.items.forEach((item: G6AddNodeMessage) => {
        const g6Node = item.node;
        const hash = nodeIdToHashable(g6Node.dataNode.id);
        // look for a node within the adjacency list that is not a newly added node
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        for (const neighbourHash of Array.from(this.adjacencyLists.get(hash))) {
          const neighbour = nodeIdFromHashable(neighbourHash);
          if (!newNodesSet.has(neighbourHash)) {
            // i is used to ensure no nodes are on top of each other.
            const newPos = g6Wrapper.getExpansionNodePositions(neighbour, 10)[i % 10];
            i++;
            setG6NodePosition(g6Node, newPos);
            break;
          }
        }
      });
    });

    // generate messages to add edges
    const addEdgeMessages = targetEdges
      .filter((edge) => !this.edges.has(nodeIdPairToHashable(edge.id1, edge.id2)))
      .map((edge) => this.addEdgeWithoutCanvasUpdate(edge));

    // update seed set before calculating node exclusions
    this.setSeedNodes(seedNodes.map((node) => node.id));

    // propogate changes to g6Wrapper and Update the canvas accordingly.
    const combinedMessage = combineGraphUpdateMessages([
      ...addNodeMessages,
      ...updateNodeMessages,
      ...removeNodeMessages,
      ...addEdgeMessages,
      this.computeExclusions(),
    ]);
    this.canvasUpdateFn(combinedMessage);

    this.reactBroadcast();

    this.setCompleteHydrationData(hydrationData);
  }

  bulkAddNodesAndEdges(nodeArguments: AddNodeArguments[], edges: Edge[]) {
    const updateNewNodesMessages = this.clearNewNodesStatus();
    const messages: G6GraphUpdateMessage[] = [];

    nodeArguments.forEach(
      ({ node, nonfetchedEdgeCount, lightlyHydratedNode, position }: AddNodeArguments) => {
        // Always add the node to the internal state tracker...
        const updateMessage = this.addNodeWithoutCanvasUpdate(
          node,
          nonfetchedEdgeCount,
          lightlyHydratedNode,
          position,
        );
        messages.push(updateMessage);
      },
    );

    edges.forEach((edge) => {
      // Always add the edge to the internal state tracker...
      const addEdgeMessage = this.addEdgeWithoutCanvasUpdate(edge);
      messages.push(addEdgeMessage);
    });

    // Update the canvas accordingly.
    const combinedMessage = combineGraphUpdateMessages([
      ...messages,
      ...updateNewNodesMessages,
      this.computeExclusions(),
    ]);

    // Update the canvas accordingly.
    this.canvasUpdateFn(combinedMessage);
  }

  // ---------------------------------------------------------------------------------------
  // Checking the state of the graph nodes and edges.
  // ---------------------------------------------------------------------------------------

  isEmptyAddNodeMessage(nodeId: NodeId) {
    // include empty add node message when graph contains node but not when node was hidden due to parsed view
    return (
      this.containsNode(nodeId) && (!this.viewTypeSwitchEnabled || !this.hiddenParsedNode(nodeId))
    );
  }
  containsNode(nodeId: NodeId) {
    return this.nodes.has(nodeIdToHashable(nodeId));
  }
  hiddenParsedNode(nodeId: NodeId) {
    if (!this.parsedNodesIdToNodes.size) return false;

    return this.parsedNodesIdToNodes.has(nodeIdToHashable(nodeId));
  }
  containsEdge(nodeId1: NodeId, nodeId2: NodeId) {
    return this.adjacencyLists.get(nodeIdToHashable(nodeId1))?.has(nodeIdToHashable(nodeId2));
  }
  getNodeIds = (): NodeId[] => {
    return Array.from(this.nodes.keys()).map(nodeIdFromHashable);
  };
  getUncollapsedNodeIds = (): NodeId[] => {
    return Array.from(this.nodes.keys())
      .filter((nodeId) => !this.nodeIdToCollapsedGroup.has(nodeId))
      .map(nodeIdFromHashable);
  };
  isNodeVisible(nodeId: NodeId) {
    const node = this.nodes.get(nodeIdToHashable(nodeId));
    return node && node.state.visible;
  }

  isPropertySelected = (datumTypeToken: DatumTypeToken): boolean => {
    return this.selectedProperties.has(datumTypeToken);
  };

  areAllPropertiesSelected = (): boolean => {
    return this.selectedProperties.size === this.nodeRenderConfigsByToken.size;
  };

  areAnyPropertiesSelected = (): boolean => {
    return this.selectedProperties.size !== 0;
  };

  // ---------------------------------------------------------------------------------------
  // Selectables
  // ---------------------------------------------------------------------------------------
  public getSelectables = (): CanvasSelectable[] => {
    return Array.from(this.selected).map(selectableFromHashable);
  };

  public getSelectedNodes = (): NodeId[] => {
    return extractSelectedNodeIds(this.getSelectables());
  };

  // selects all entities in selectables, and deselects all other entities
  public setSelectables = (selectables: CanvasSelectable[] = []) => {
    const selectedNodeIds = extractSelectedNodeIds(selectables).map(nodeIdToHashable);

    const changedNodes = [];
    for (const [nodeId, node] of this.nodes) {
      // do not set selectability of nodes inside a collapsed-group
      if (this.nodeIdToCollapsedGroup.has(nodeId)) {
        continue;
      }
      if (selectedNodeIds.includes(nodeId)) {
        if (!node.state.selected) {
          const selectable = nodeIdToSelectable(nodeIdFromHashable(nodeId));
          this.selected.add(selectableToHashable(selectable));
          node.state.selected = true;
          changedNodes.push(node);
        }
      } else {
        if (node.state.selected) {
          const selectable = nodeIdToSelectable(nodeIdFromHashable(nodeId));
          this.selected.delete(selectableToHashable(selectable));
          node.state.selected = false;
          changedNodes.push(node);
        }
      }
    }
    const nodesMessage = this.newUpdateNodesMessage(changedNodes);

    const selectedCollapsedGroupIds = extractSelectedCollapsedGroupIds(selectables);
    const changedCollapsedGroups = [];
    for (const [collapsedGroupId, collapsedGroup] of this.collapsedGroups) {
      if (selectedCollapsedGroupIds.includes(collapsedGroupId)) {
        if (!collapsedGroup.state.selected) {
          const selectable = collapsedGroupIdToSelectable(collapsedGroupId);
          this.selected.add(selectableToHashable(selectable));
          collapsedGroup.state.selected = true;
          changedCollapsedGroups.push(collapsedGroup);
        }
      } else {
        if (collapsedGroup.state.selected) {
          const selectable = collapsedGroupIdToSelectable(collapsedGroupId);
          this.selected.delete(selectableToHashable(selectable));
          collapsedGroup.state.selected = false;
          changedCollapsedGroups.push(collapsedGroup);
        }
      }
    }
    const collapsedGroupsMessage = this.newUpdateCollapsedGroupsMessage(changedCollapsedGroups);

    const selectedCollapsedPairwiseNodeIds = extractSelectedCollapsedPairwiseNodeIds(selectables);
    const changedCollapsedPairwiseNodes = [];
    for (const [collapsedNodeId, collapsedNode] of this.collapsedPairwiseNodes) {
      if (
        selectedCollapsedPairwiseNodeIds.includes(collapsedNodeId) &&
        !collapsedNode.state.selected
      ) {
        const selectable = collapsedPairwiseNodeIdToSelectable(collapsedNodeId);
        this.selected.add(selectableToHashable(selectable));
        collapsedNode.state.selected = true;
        changedCollapsedPairwiseNodes.push(collapsedNode);
      } else if (collapsedNode.state.selected) {
        const selectable = collapsedPairwiseNodeIdToSelectable(collapsedNodeId);
        this.selected.delete(selectableToHashable(selectable));
        collapsedNode.state.selected = false;
        changedCollapsedPairwiseNodes.push(collapsedNode);
      }
    }
    const collapsedNodesMessage = this.newUpdateCollapsedPairwiseNodesMessage(
      changedCollapsedPairwiseNodes,
    );

    const message = combineGraphUpdateMessages([
      collapsedGroupsMessage,
      nodesMessage,
      collapsedNodesMessage,
    ]);
    this.canvasUpdateFn(message);
    this.reactBroadcast();
    this.broadcastSelectablesChange(Array.from(this.selected).map(selectableFromHashable));
    // Logic for triggering the right Segment event
    const isGroupSelectionChange = selectables.length > 1;
    if (isGroupSelectionChange) {
      changedNodes[0]?.state.selected
        ? SegmentClient.sendSegmentTrackEvent('Graph - Select Group')
        : SegmentClient.sendSegmentTrackEvent('Graph - Deselect Group');
    } else {
      changedNodes[0]?.state.selected
        ? SegmentClient.sendSegmentTrackEvent('Graph - Select Individual')
        : SegmentClient.sendSegmentTrackEvent('Graph - Deselect Individual');
    }
  };

  // all selectables not passed are unchanged
  public toggleSelectables = (selectables: CanvasSelectable[] = [], selected: boolean) => {
    const selectedNodeIds = extractSelectedNodeIds(selectables);

    const changedNodes = [];
    for (const nodeId of selectedNodeIds) {
      // do not toggle selectability of nodes inside a collapsed-group
      if (this.nodeIdToCollapsedGroup.has(nodeIdToHashable(nodeId))) {
        continue;
      }
      const node = this.getNodeById(nodeIdToHashable(nodeId));
      if (selected && !node.state.selected) {
        const selectable = nodeIdToSelectable(nodeId);
        this.selected.add(selectableToHashable(selectable));
        node.state.selected = true;
        changedNodes.push(node);
      } else if (!selected && node.state.selected) {
        const selectable = nodeIdToSelectable(nodeId);
        this.selected.delete(selectableToHashable(selectable));
        node.state.selected = false;
        changedNodes.push(node);
      }
    }
    const nodesMessage = this.newUpdateNodesMessage(changedNodes);

    const changedCollapsedGroups = [];
    const selectedCollapsedGroupIds = extractSelectedCollapsedGroupIds(selectables);
    for (const groupId of selectedCollapsedGroupIds) {
      const collapsedGroup = this.getCollapsedGroupById(groupId);
      if (selected && !collapsedGroup.state.selected) {
        const selectable = collapsedGroupIdToSelectable(groupId);
        this.selected.add(selectableToHashable(selectable));
        collapsedGroup.state.selected = true;
        changedCollapsedGroups.push(collapsedGroup);
      } else if (!selected && collapsedGroup.state.selected) {
        const selectable = collapsedGroupIdToSelectable(groupId);
        this.selected.delete(selectableToHashable(selectable));
        collapsedGroup.state.selected = false;
        changedCollapsedGroups.push(collapsedGroup);
      }
    }
    const collapsedGroupsMessage = this.newUpdateCollapsedGroupsMessage(changedCollapsedGroups);

    const message = combineGraphUpdateMessages([collapsedGroupsMessage, nodesMessage]);
    this.canvasUpdateFn(message);
    this.reactBroadcast();
    this.broadcastSelectablesChange(Array.from(this.selected).map(selectableFromHashable));
    // Logic for triggering the right Segment event
    const isGroupSelectionChange = selectables.length > 1;
    if (isGroupSelectionChange) {
      changedNodes[0]?.state.selected
        ? SegmentClient.sendSegmentTrackEvent('Graph - Select Group')
        : SegmentClient.sendSegmentTrackEvent('Graph - Deselect Group');
    } else {
      changedNodes[0]?.state.selected
        ? SegmentClient.sendSegmentTrackEvent('Graph - Select Individual')
        : SegmentClient.sendSegmentTrackEvent('Graph - Deselect Individual');
    }
  };

  private broadcastSelectablesChange = (selectables: CanvasSelectable[]) => {
    for (const [_, handler] of this.onSelectablesChangeHandlers) {
      handler(selectables);
    }
  };

  public offSelectablesChange = (name: string) => {
    this.onSelectablesChangeHandlers.delete(name);
  };

  public onSelectablesChange = (handler: CanvasSelectableChangeHandlerFn, name: string) => {
    this.onSelectablesChangeHandlers.set(name, handler);
  };

  isNodeSelected = (nodeId: NodeId) => {
    return this.selected.has(selectableToHashable(nodeIdToSelectable(nodeId)));
  };

  isCollapsedGroupSelected = (collapsedGroupId: CollapsedGroupId) => {
    return this.selected.has(selectableToHashable(collapsedGroupIdToSelectable(collapsedGroupId)));
  };

  isSelected = (selectable: CanvasSelectable) => {
    return this.selected.has(selectableToHashable(selectable));
  };

  // set selection of all nodes of type datumTypeToken. All other nodes left untouched.
  public selectDatumTypeNodes = (datumTypeToken: DatumTypeToken, selected: boolean) => {
    const selectablesToToggle = this.getNodesByDatumType(datumTypeToken).map(nodeIdToSelectable);
    this.toggleSelectables(selectablesToToggle, selected);
  };

  public isDatumTypeNodesSelected = (datumTypeToken: DatumTypeToken): boolean => {
    const allNodesOfType = this.getNodesByDatumType(datumTypeToken);
    if (allNodesOfType.length === 0) {
      return false;
    }
    const result = Array.from(allNodesOfType).filter((nodeId) => {
      return this.isNodeSelected(nodeId);
    });
    return result.length === allNodesOfType.length;
  };

  selectNode = (nodeId: NodeId, selected: boolean) => {
    this.toggleSelectables([nodeIdToSelectable(nodeId)], selected);
  };

  // ---------------------------------------------------------------------------------------
  // Node highlighting.
  // ---------------------------------------------------------------------------------------
  public isHighlighted = (nodeId: NodeId) => {
    return this.highlightedNodes.has(nodeIdToHashable(nodeId));
  };

  private setNodeHighlighted(nodeId: NodeId, highlighted: boolean) {
    const hashableNodeId = nodeIdToHashable(nodeId);
    if (highlighted) {
      this.highlightedNodes.add(hashableNodeId);
    } else {
      this.highlightedNodes.delete(hashableNodeId);
    }
    const node = this.nodes.get(hashableNodeId);
    // TODO: Consolidate states to use G6 state manager
    if (node) {
      node.state.highlighted = highlighted;

      // Update the canvas accordingly.
      this.canvasUpdateFn(this.newUpdateNodeMessage(node));
    }
  }

  public setHighlightedNodes(nodeIds: NodeId[]) {
    const highlightedNodeIds = nodeIds.map(nodeIdToHashable);

    for (const [nodeId, node] of this.nodes) {
      if (highlightedNodeIds.includes(nodeId)) {
        if (!node.state.highlighted) {
          this.setNodeHighlighted(node.id, true);
        }
      } else {
        if (node.state.highlighted) {
          this.setNodeHighlighted(node.id, false);
        }
      }
    }

    this.reactBroadcast();
  }

  private setCollapsedGroupHighlighted(collapsedGroupId: CollapsedGroupId, highlighted: boolean) {
    if (highlighted) {
      this.highlightedCollapsedGroups.add(collapsedGroupId);
    } else {
      this.highlightedCollapsedGroups.delete(collapsedGroupId);
    }
    const collapsedGroup = this.collapsedGroups.get(collapsedGroupId);
    if (collapsedGroup) {
      collapsedGroup.state.highlighted = highlighted;
      // Update the canvas accordingly.
      this.canvasUpdateFn(this.newUpdateCollapsedGroupMessage(collapsedGroup));
    }
  }

  private setCollapsedPairwiseNodeHighlighted(
    collapsedNodeId: CollapsedPairwiseNodeId,
    highlighted: boolean,
  ) {
    if (highlighted) {
      this.highlightedCollapsedPairwiseNodes.add(collapsedNodeId);
    } else {
      this.highlightedCollapsedPairwiseNodes.delete(collapsedNodeId);
    }
    const collapsedNode = this.collapsedPairwiseNodes.get(collapsedNodeId);
    if (collapsedNode) {
      collapsedNode.state.highlighted = highlighted;
      // Update the canvas accordingly.
      this.canvasUpdateFn(this.newUpdateCollapsedPairwiseNodeMessage(collapsedNode));
    }
  }

  highlightCollapsedGroup = (collapsedGroupId: CollapsedGroupId, highlighted: boolean) => {
    this.setCollapsedGroupHighlighted(collapsedGroupId, highlighted);
  };

  highlightCollapsedPairwiseNode = (
    collapsedNodeId: CollapsedPairwiseNodeId,
    highlighted: boolean,
  ) => {
    this.setCollapsedPairwiseNodeHighlighted(collapsedNodeId, highlighted);
  };

  highlightNode = (nodeId: NodeId, highlighted: boolean) => {
    this.setNodeHighlighted(nodeId, highlighted);
  };

  highlightDatumTypeNodes = (datumTypeToken: DatumTypeToken, highlighted: boolean) => {
    Array.from(this.nodes.keys()).forEach((hashableNodeId) => {
      const nodeId = nodeIdFromHashable(hashableNodeId as HashableNodeId);
      if (nodeId.token === datumTypeToken) {
        this.setNodeHighlighted(nodeId, highlighted);
      }
    });
  };

  // ---------------------------------------------------------------------------------------
  // Node dimming.
  // ---------------------------------------------------------------------------------------

  isDimmed = (nodeId: NodeId) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.nodes.get(nodeIdToHashable(nodeId)).state.dimmed === true;
  };

  private setNodeDimmed = (nodeId: NodeId, dimmed: boolean) => {
    // break early if node is inside collapsed node
    const hashableNodeId = nodeIdToHashable(nodeId);
    if (this.nodeIdToCollapsedGroup.has(hashableNodeId)) {
      return;
    }

    const node = this.nodes.get(hashableNodeId);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    node.state.dimmed = dimmed;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const messages: G6GraphUpdateMessage[] = [this.newUpdateNodeMessage(node)];

    const visitedNeighborNodes = new Set([hashableNodeId]);
    // Check adjacent edges and nodes, and see if they should also be (un)dimmed.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    for (const neighborHashableNodeId of this.adjacencyLists.get(hashableNodeId)) {
      if (visitedNeighborNodes.has(neighborHashableNodeId)) {
        break;
      }
      visitedNeighborNodes.add(neighborHashableNodeId);
      const neighborNodeId = nodeIdFromHashable(neighborHashableNodeId);
      const edge = this.edges.get(nodeIdPairToHashable(nodeId, neighborNodeId));
      const neighborNode = this.nodes.get(neighborHashableNodeId);
      // If there are no other visible (un)dimmed neighboring nodes, (un)dim this one as well
      if (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        Array.from(this.adjacencyLists.get(neighborHashableNodeId)).filter(
          (neighborOfNeighborHashableNodeId) =>
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.nodes.get(neighborOfNeighborHashableNodeId).state.dimmed !== dimmed &&
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.nodes.get(neighborOfNeighborHashableNodeId).state.visible &&
            neighborOfNeighborHashableNodeId !== hashableNodeId,
        ).length === 0
      ) {
        // only call setNodeDimmed if the state actually needs to be changed, to avoid extra looping
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (neighborNode.state.dimmed !== dimmed) {
          this.setNodeDimmed(neighborNodeId, dimmed);
        }
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      messages.push(this.newUpdateEdgeMessage(edge));
    }

    // Update the canvas accordingly.
    this.canvasUpdateFn(combineGraphUpdateMessages(messages));
  };

  dimNode = (nodeId: NodeId, dimmed: boolean) => {
    this.setNodeDimmed(nodeId, dimmed);
  };

  setDimmedProperty = (datumTypeToken: DatumTypeToken, dimmed: boolean) => {
    if (dimmed) {
      this.dimmedProperties.add(datumTypeToken);
    } else {
      this.dimmedProperties.delete(datumTypeToken);
    }
  };

  // sets the dimmedProperty, then sets the dimmed value of all nodes
  dimDatumTypeNodes = (datumTypeToken: DatumTypeToken, dimmed: boolean) => {
    this.setDimmedProperty(datumTypeToken, dimmed);

    this.reactBroadcast();

    Array.from(this.nodes.keys()).forEach((hashableNodeId) => {
      const nodeId = nodeIdFromHashable(hashableNodeId as HashableNodeId);
      if (nodeId.token === datumTypeToken) {
        this.setNodeDimmed(nodeId, dimmed);
      }
    });
  };

  isDatumTypeDimmed = (datumTypeToken: DatumTypeToken) => {
    return this.dimmedProperties.has(datumTypeToken);
  };

  // ---------------------------------------------------------------------------------------
  // Node visibility.
  // ---------------------------------------------------------------------------------------

  private setNodeVisibility(nodeId: NodeId, visible: boolean) {
    const hashableNodeId = nodeIdToHashable(nodeId);
    const node = this.nodes.get(hashableNodeId);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    node.state.visible = visible;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const messages: G6GraphUpdateMessage[] = [this.newUpdateNodeMessage(node)];
    // Check adjacent edges, and see if they should also be hidden or shown.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId).forEach((neighborHashableNodeId) => {
      let neighborNodeId = nodeIdFromHashable(neighborHashableNodeId);
      const [normalizedNodeId1, normalizedNodeId2] = normalizeNodeIdPair(nodeId, neighborNodeId);
      [nodeId, neighborNodeId] = [normalizedNodeId1, normalizedNodeId2];
      let edge = this.edges.get(nodeIdPairToHashable(nodeId, neighborNodeId));

      // TODO (jes) why doesn't normalizing work?
      if (edge == null) {
        edge = this.edges.get(nodeIdPairToHashable(neighborNodeId, nodeId));
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      messages.push(this.newUpdateEdgeMessage(edge));
    });

    // Update the canvas accordingly.
    this.canvasUpdateFn(combineGraphUpdateMessages(messages));
  }

  showNode(nodeId: NodeId) {
    this.setNodeVisibility(nodeId, true);
  }

  hideNode(nodeId: NodeId) {
    this.setNodeVisibility(nodeId, false);
  }

  // ---------------------------------------------------------------------------------------
  // Node expansion.
  // ---------------------------------------------------------------------------------------

  public setNodeLoading(nodeId: NodeId, loading: boolean) {
    const node = this.nodes.get(nodeIdToHashable(nodeId));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    node.state.loading = loading;

    // Update the canvas accordingly.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.canvasUpdateFn(this.newUpdateNodeMessage(node));
  }

  decrementNonfetchedEdgeCountWithoutCanvasUpdate(nodeIds: NodeId[]) {
    return nodeIds.map((nodeId) => {
      const hashableNodeId = nodeIdToHashable(nodeId);
      let nonfetchedEdgeCount = this.nonfetchedEdgeCounts.get(hashableNodeId) ?? 0;
      // The currently stored nonfetchedEdgeCount may be stale. The size of the graph may have increased
      // since we last run the graph query. Make sure we clamp the nonfetchedEdgeCount to 0.
      nonfetchedEdgeCount = Math.max(0, nonfetchedEdgeCount - 1);
      this.nonfetchedEdgeCounts.set(hashableNodeId, nonfetchedEdgeCount);

      const node = this.nodes.get(nodeIdToHashable(nodeId));
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      node.state.nonfetchedEdgeCount = nonfetchedEdgeCount;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return this.newUpdateNodeMessage(node);
    });
  }

  decrementNonfetchedEdgeCount(nodeIds: NodeId[]) {
    this.canvasUpdateFn(
      combineGraphUpdateMessages(this.decrementNonfetchedEdgeCountWithoutCanvasUpdate(nodeIds)),
    );
  }

  // ---------------------------------------------------------------------------------------
  // Node hydration.
  // ---------------------------------------------------------------------------------------

  setHydrationData(nodeId: NodeId, hydratedNode: HydratedNode) {
    this.hydrationData.set(nodeIdToHashable(nodeId), hydratedNode);

    this.reactBroadcast();
  }

  getHydrationData = (nodeId: NodeId): HydratedNode | null => {
    return this.hydrationData.get(nodeIdToHashable(nodeId)) ?? null;
  };

  getLightlyHydratedData = (nodeId: NodeId): LightlyHydratedNode | null => {
    return this.lightHydrationData.get(nodeIdToHashable(nodeId)) ?? null;
  };

  getPairwiseMemberNodeIds = (collapsedPairwiseNodeId: CollapsedPairwiseNodeId): NodeId[] => {
    const collapsedPairwiseNode = this.getCollapsedPairwiseNodeById(collapsedPairwiseNodeId);
    return Array.from(collapsedPairwiseNode.members).map(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (hashableId) => this.nodes.get(hashableId).id,
    );
  };

  getCompleteHydrationData = () => {
    return this.hydrationData;
  };

  setCompleteHydrationData = (hydrationData: Map<HashableNodeId, HydratedNode>): void => {
    this.hydrationData = hydrationData;
  };

  // ---------------------------------------------------------------------------------------
  // Node exclusions
  // ---------------------------------------------------------------------------------------
  public setExcludedNodes = (excludedNodes: Array<NodeId> = []) => {
    this.excludedNodes = new Set(excludedNodes.map(nodeIdToHashable));
  };

  public addExcludedNodes = (excludedNodesToAdd: Array<NodeId> = []) => {
    this.excludedNodes = new Set([
      ...this.excludedNodes,
      ...excludedNodesToAdd.map(nodeIdToHashable),
    ]);
  };

  public removeExcludedNodes = (excludedNodesToRemove: Array<NodeId> = []) => {
    const excludedHashableNodeIds = excludedNodesToRemove.map(nodeIdToHashable);
    this.excludedNodes = new Set(
      Array.from(this.excludedNodes).filter(
        (excludedNode) => !excludedHashableNodeIds.includes(excludedNode),
      ),
    );
  };

  public setSeedNodes = (seedNodes: Array<NodeId> = []) => {
    this.seedNodes = new Set(seedNodes.map(nodeIdToHashable));
  };

  public showExcludedNodes = (): G6GraphUpdateMessage => {
    this.excludedNodesVisible = true;
    this.reactBroadcast();
    return this.computeExclusions();
  };

  public hideExcludedNodes = (): G6GraphUpdateMessage => {
    this.excludedNodesVisible = false;
    this.reactBroadcast();
    return this.computeExclusions();
  };

  public getExcludedNodes = (): NodeId[] => {
    return Array.from(this.excludedNodes).map(nodeIdFromHashable);
  };

  public getHiddenNodes = (): NodeId[] => {
    return Array.from(this.hiddenNodes).map(nodeIdFromHashable);
  };

  public getVisibleNodes = (): NodeId[] => {
    return Array.from(this.visibleNodes).map(nodeIdFromHashable);
  };

  public computeExclusions(): G6GraphUpdateMessage {
    if (this.seedNodes.size === 0 || this.adjacencyLists.size === 0) {
      this.visibleNodes = new Set(this.nodes.keys());
      this.hiddenNodes = new Set();
      return newEmptyGraphUpdateMessage();
    }

    const visibleNodes: Set<HashableNodeId> = new Set();
    const seenSet: Set<HashableNodeId> = new Set(
      Array.from(this.seedNodes).filter((seedNode) => this.excludedNodes.has(seedNode)),
    );
    const toVisit = Array.from(this.seedNodes).filter(
      (hashableNodeId) => !this.excludedNodes.has(hashableNodeId),
    );

    let cursor = 0;
    let current = toVisit[cursor];
    while (cursor < toVisit.length) {
      seenSet.add(current);
      visibleNodes.add(current);

      const neighbors = this.adjacencyLists.get(current);
      if (neighbors) {
        Array.from(neighbors)
          .filter(
            (hashableNodeId) =>
              !this.excludedNodes.has(hashableNodeId) && !seenSet.has(hashableNodeId),
          )
          .forEach((neighbor) => toVisit.push(neighbor));
      }

      cursor += 1;
      current = toVisit[cursor];
    }

    this.visibleNodes = new Set(visibleNodes);
    this.hiddenNodes = new Set(
      Array.from(this.nodes.keys()).filter(
        (hashableNodeId: HashableNodeId) => !this.visibleNodes.has(hashableNodeId),
      ),
    );

    const messages = [];

    for (const [hashableNodeId, node] of this.nodes) {
      // NB: `this.visibleNodes` refers to all nodes that are not "hidden" due to node exclusion
      // whereas `node.state.visible` determines whether or not to make it actually visible on
      // canvas. Sometimes the user may want to look at hiddenNodes, so hidden nodes may have
      // node.state.visible = true.
      if (this.visibleNodes.has(hashableNodeId)) {
        node.state.visible = true;
        node.state.dimmed = false;
        node.state.excluded = false;
      }

      if (this.hiddenNodes.has(hashableNodeId)) {
        if (this.excludedNodesVisible) {
          node.state.visible = true;
          if (this.excludedNodes.has(hashableNodeId)) {
            node.state.excluded = true;
            node.state.dimmed = false;
          } else {
            node.state.excluded = false;
            node.state.dimmed = true;
          }
        } else {
          node.state.visible = false;
        }
      }

      messages.push(this.newUpdateNodeMessage(node));

      const neighbors = this.adjacencyLists.get(hashableNodeId);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      for (const neighborHashableNodeId of neighbors) {
        const edge = this.edges.get(
          hashableNodeIdPairToHashable(hashableNodeId, neighborHashableNodeId),
        );
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        messages.push(this.newUpdateEdgeMessage(edge));
      }
    }

    this.reactBroadcast();
    return combineGraphUpdateMessages(messages);
  }

  // ---------------------------------------------------------------------------------------
  // Graph data stats
  // ---------------------------------------------------------------------------------------
  getVertexTypeCount = (datumTypeToken: DatumTypeToken) => {
    let count = 0;
    for (const hashableNodeId of Array.from(this.nodes.keys())) {
      if (nodeIdFromHashable(hashableNodeId).token === datumTypeToken) {
        count++;
      }
    }
    return count;
  };

  getAccountTagCount = (tag: string) => {
    return Array.from(this.hydrationData.entries())
      .filter(
        ([hashableNodeId, _hydrationData]) =>
          nodeIdFromHashable(hashableNodeId).token === ACCOUNT_DATUM_TYPE.token,
      )
      .filter(([_hashableNodeId, hydrationData]) => {
        switch (hydrationData.nodeType) {
          case 'account':
            return hydrationData.hydrationData.tags.includes(tag);
          default:
            throw new Error(`Expected account data, got: ${hydrationData.nodeType}`);
        }
      }).length;
  };

  // ---------------------------------------------------------------------------------------
  // Canvas zoom management
  // ---------------------------------------------------------------------------------------
  getZoomPercentage = (): number => {
    return this.zoomPercentage;
  };

  setZoomPercentage = (zoomPercentage: number): void => {
    this.zoomPercentage = zoomPercentage;

    this.reactBroadcast();
  };

  // Currently view type can only be 'Parsed' or 'Expanded'.
  // The type will only be 'Parsed' when all cliques are parsed on the canvas, otherwise it will be 'Expanded'
  setViewType = (viewType: string, g6Wrapper: G6Wrapper): void => {
    if (viewType === GraphViewTypes.Parsed) {
      if (this.selectedViewType === viewType) {
        // this means the graph canvas is already parsed
        return;
      }
      this.addCollapsedPairwiseNodes(g6Wrapper);
    } else if (viewType === GraphViewTypes.Expanded) {
      this.removeCollapsedPairwiseNodes(g6Wrapper);
      this.expandedCliqueIds = new Set(this.cliques.keys());
      this.collapsedCliqueIds.clear();
    }

    this.selectedViewType = viewType;
  };

  getParseView = (): string => {
    return this.selectedViewType;
  };

  zoomIn = (): void => {
    const value = zoomInFrom(this.getZoomPercentage());
    if (value) {
      this.setZoomPercentage(value);
    }
  };

  zoomOut = (): void => {
    const value = zoomOutFrom(this.getZoomPercentage());
    if (value) {
      this.setZoomPercentage(value);
    }
  };

  canZoomIn = (): boolean => {
    return zoomInFrom(this.getZoomPercentage()) != null;
  };

  canZoomOut = (): boolean => {
    return zoomOutFrom(this.getZoomPercentage()) != null;
  };

  // ---------------------------------------------------------------------------------------
  // Context menu state
  // ---------------------------------------------------------------------------------------
  getHoveredSelectable = (): CanvasSelectable | null => {
    return this.hoverCardState?.hovered;
  };

  getHoverCardX = (): number | null => {
    return this.hoverCardState?.canvasX;
  };

  getHoverCardY = (): number | null => {
    return this.hoverCardState?.canvasY;
  };

  isHoverCardOpen = (): boolean => {
    return this.hoverCardState != null;
  };

  getHoveredNodeId = (): NodeId => {
    const selectable = this.getHoveredSelectable();
    if (selectable?.type === RenderedCanvasNodeType.Node) {
      return selectable.selectable;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  };

  getHoveredCollapsedGroupId = (): CollapsedGroupId => {
    const selectable = this.getHoveredSelectable();
    if (selectable?.type === RenderedCanvasNodeType.CollapsedGroup) {
      return selectable.selectable;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  };

  getHoveredCollapsedPairwiseNodeId = (): CollapsedPairwiseNodeId => {
    const selectable = this.getHoveredSelectable();
    if (selectable?.type === RenderedCanvasNodeType.CollapsedPairwiseNode) {
      return selectable.selectable;
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  };

  setHoverCard = (selectable: CanvasSelectable, x: number, y: number) => {
    this.hoverCardState = {
      canvasX: x,
      canvasY: y,
      hovered: selectable,
    };

    this.reactBroadcast();
  };

  closeHoverCard = () => {
    this.hoverCardState = emptyHoverCard();

    this.reactBroadcast();
  };

  // ---------------------------------------------------------------------------------------
  // Custom datum types
  // ---------------------------------------------------------------------------------------
  setGraphConfiguration = (
    nodeDatumTypes: DatumType[],
    coloringRules: ColorRule[],
    nodeRenderConfigsByToken: Map<DatumTypeToken, NodeRenderConfig>,
    propertyConfigs: PropertyConfig[],
  ) => {
    this.nodeDatumTypesByToken.clear();
    nodeDatumTypes.forEach((datumType) => {
      this.nodeDatumTypesByToken.set(datumType.token, datumType);
      // The custom datum type is selected by default.
      this.selectedProperties.add(datumType.token);
    });

    this.coloringRules = coloringRules;
    this.nodeRenderConfigsByToken = new Map(nodeRenderConfigsByToken.entries());
    this.propertyConfigs = propertyConfigs;
  };

  getNodeDatumTypes = (): DatumType[] => {
    return Array.from(this.nodeDatumTypesByToken.values());
  };

  getPropertyConfigs = (): PropertyConfig[] => {
    return this.propertyConfigs;
  };

  getNodeConditions = (
    hydrationData: ServerLightlyHydratedAccount,
    isFeaturedNode: boolean,
  ): string[] => {
    const conditions: string[] = [];
    if (isFeaturedNode) conditions.push(NodeConditions.SeedSet);
    if (!hydrationData['has-completed-inquiries'])
      conditions.push(NodeConditions.NoCompletedInquiries);
    return conditions;
  };

  getAccountNodeColor = (
    hydrationData: ServerLightlyHydratedAccount,
    isFeaturedNode: boolean,
  ): string => {
    // Graceful fallback, use a default color
    if (!hydrationData) {
      return Color.CorePurple600;
    }
    const normalizedTags = hydrationData.tags.map((tag) => tag.toLowerCase());
    const normalizedStatus = hydrationData.status.toLowerCase();
    const nodeConditions = this.getNodeConditions(hydrationData, isFeaturedNode);
    for (const rule of this.coloringRules) {
      switch (rule.type) {
        case 'status': {
          if ((rule as ColorRuleStatus).status.toLowerCase() === normalizedStatus && rule.color) {
            return rule.color.hex();
          }
          break;
        }
        case 'tag': {
          if (normalizedTags.includes((rule as ColorRuleTag).tag.toLowerCase()) && rule.color) {
            return rule.color.hex();
          }
          break;
        }
        case 'condition': {
          if (nodeConditions.includes((rule as ColorRuleCondition).condition) && rule.color) {
            return rule.color.hex();
          }
          break;
        }
        default: {
          assertNeverAndThrow(rule);
        }
      }
    }
    if (nodeConditions.includes(NodeConditions.NoCompletedInquiries)) return Color.CorePurple200;
    return Color.CorePurple600;
  };

  getDatumTypeByToken = (token: DatumTypeToken): DatumType => {
    if (PERSONA_DATUM_TYPE_TO_DATUM_TYPE.has(token)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return PERSONA_DATUM_TYPE_TO_DATUM_TYPE.get(token);
    }
    if (this.nodeDatumTypesByToken.has(token)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return this.nodeDatumTypesByToken.get(token);
    }
    throw new Error(`Failed to lookup datum type token: ${token}`);
  };

  getNodeRenderConfig = (token: DatumTypeToken): NodeRenderConfig => {
    if (this.nodeRenderConfigsByToken.has(token)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return this.nodeRenderConfigsByToken.get(token);
    }
    switch (token) {
      case ACCOUNT_DATUM_TYPE.token:
        return DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG;
      case TRANSACTIONS_DATUM_TYPE.token:
        return TRANSACTIONS_DATUM_TYPE_RENDER_CONFIG;
      case EXTERNAL_ACCOUNT_DATUM_TYPE.token:
        return EXTERNAL_ACCOUNT_DATUM_TYPE_RENDER_CONFIG;
      case RenderedCanvasNodeType.CollapsedPairwiseNode:
        return COLLAPSED_PAIRWISE_NODE_TYPE_RENDER_CONFIG;
      default:
        return UNKNOWN_DATUM_TYPE_RENDER_CONFIG;
    }
  };

  // ---------------------------------------------------------------------------------------
  // Collapsed Group Logic
  // ---------------------------------------------------------------------------------------
  // transform a list of g6Edges so they point to correct g6Nodes on the canvas
  // For example, if one of the nodes in a g6Edge belongs to a collapsedGroup, we point it
  // to that collapsed group instead.
  // We remove duplicate edges (same source and target) and edges whose source=target
  retargetG6Edges = (g6Edges: G6Edge[]): G6Edge[] => {
    // if edge should instead be pointing to collapsed group, point to that instead
    for (const g6Edge of g6Edges) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const sourceNodeId = g6IdToNodeId(g6Edge.source);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const targetNodeId = g6IdToNodeId(g6Edge.target);
      if (!sourceNodeId || !targetNodeId) {
        continue;
      }

      let edgeRetargeted = false;

      const hashableSourceNodeId = nodeIdToHashable(sourceNodeId);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      let sourceG6Id: G6NodeId = g6Edge.source;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      let sourceNodeState: NodeState = this.nodes.get(hashableSourceNodeId).state;
      if (this.nodeIdToCollapsedGroup.has(hashableSourceNodeId)) {
        const groupId = this.nodeIdToCollapsedGroup.get(hashableSourceNodeId);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sourceNodeState = this.collapsedGroups.get(groupId).state;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sourceG6Id = collapsedGroupIdToG6Id(groupId);
        edgeRetargeted = true;
      }

      const hashableTargetNodeId = nodeIdToHashable(targetNodeId);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      let targetG6Id: G6NodeId = g6Edge.target;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      let targetNodeState: NodeState = this.nodes.get(hashableTargetNodeId).state;
      if (this.nodeIdToCollapsedGroup.has(hashableTargetNodeId)) {
        const groupId = this.nodeIdToCollapsedGroup.get(hashableTargetNodeId);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        targetNodeState = this.collapsedGroups.get(groupId).state;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        targetG6Id = collapsedGroupIdToG6Id(groupId);
        edgeRetargeted = true;
      }

      // update the g6Edge if the source or target changed
      if (edgeRetargeted) {
        g6Edge.id = g6EdgeId(sourceG6Id, targetG6Id);
        g6Edge.source = sourceG6Id;
        g6Edge.target = targetG6Id;
        styleG6Edge(g6Edge, sourceNodeState, targetNodeState);
      }
    }
    // reject edges that point to itself. This may happen if both nodes belong to the same collapsed group
    const filteredG6Edges = g6Edges.filter((g6Edge) => g6Edge.source !== g6Edge.target);

    // reject duplicate edges. This may happen often when a node points to many nodes in the same collapsed group
    return uniqBy('id', filteredG6Edges);
  };

  removeCollapsedGroup = (collapsedGroupId: CollapsedGroupId, g6Wrapper: G6Wrapper) => {
    const collapsedGroup = this.collapsedGroups.get(collapsedGroupId);

    // update graph-explorer state
    this.selected.delete(selectableToHashable(collapsedGroupIdToSelectable(collapsedGroupId)));
    this.collapsedGroups.delete(collapsedGroupId);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    for (const nodeId of collapsedGroup.members) {
      this.nodeIdToCollapsedGroup.delete(nodeId);
    }

    // generate all graph update messages
    const removeCollapsedGroupMessage: G6GraphUpdateMessage =
      createG6RemoveCollapsedGroupMessage(collapsedGroupId);

    const g6CollapsedGroup = g6Wrapper.nodeByCollapsedGroupId(collapsedGroupId);
    const { x: absX, y: absY } = g6CollapsedGroup.getCanvasBBox();

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const addNodesMessages: G6GraphUpdateMessage[] = [...collapsedGroup.members].map((nodeId) => {
      const g6Node = nodeToG6Node(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodes.get(nodeId),
        this.lightHydrationData.get(nodeId),
        this.getNodeRenderConfig,
        this.getAccountNodeColor,
      );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const { x: deltaX, y: deltaY } = collapsedGroup.relativeNodePositions.get(nodeId);
      g6Node.x = absX + deltaX;
      g6Node.y = absY + deltaY;

      return {
        items: [
          {
            messageType: 'add_node',
            node: g6Node,
          },
        ],
      };
    });
    const edgesToAdd: Edge[] = [];
    for (const edge of this.edges.values()) {
      if (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        collapsedGroup.members.has(nodeIdToHashable(edge.id1)) ||
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        collapsedGroup.members.has(nodeIdToHashable(edge.id2))
      ) {
        edgesToAdd.push(edge);
      }
    }
    const addEdgesMessages: G6GraphUpdateMessage = this.newAddEdgesMessage(edgesToAdd);

    const message: G6GraphUpdateMessage = combineGraphUpdateMessages([
      removeCollapsedGroupMessage,
      ...addNodesMessages,
      addEdgesMessages,
    ]);
    this.canvasUpdateFn(message);
    this.setSelectables(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [...collapsedGroup.members].map(nodeIdFromHashable).map(nodeIdToSelectable),
    );
    this.reactBroadcast();
  };

  removeCollapsedPairwiseNode = (
    collapsedNodeId: CollapsedPairwiseNodeId,
    g6Wrapper: G6Wrapper,
  ) => {
    this.selectedViewType = GraphViewTypes.Expanded;

    this.expandedCliqueIds.add(collapsedNodeId);
    this.collapsedCliqueIds.delete(collapsedNodeId);
    this.selected.delete(
      selectableToHashable(collapsedPairwiseNodeIdToSelectable(collapsedNodeId)),
    );

    this.removeCollapsedPairwiseNodes(g6Wrapper, [
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.collapsedPairwiseNodes.get(collapsedNodeId),
    ]);
    this.collapsedPairwiseNodes.delete(collapsedNodeId);
  };

  private removeCollapsedPairwiseNodes = (
    g6Wrapper: G6Wrapper,
    collapsedNodes: CollapsedPairwiseNode[] = [...this.collapsedPairwiseNodes.values()],
  ) => {
    // update graph-explorer state
    let index = 0;
    const edgesToAdd: Edge[] = [];
    const addNodeMessages: G6GraphUpdateMessage[] = [];
    const propertyNodesToAdd: Set<HashableNodeId> = new Set();

    const nodePositions = g6Wrapper.getExperimentalLiveNodePositions(
      Array.from(this.parsedNodesIdToNodes.values()),
      Array.from(this.edges.values()),
    );

    collapsedNodes.forEach((node) =>
      node.members.forEach((member) => propertyNodesToAdd.add(member)),
    );

    this.parsedNodesIdToNodes.forEach((node, hashableId) => {
      // for each parse node, add them back to the canvas
      if (propertyNodesToAdd.has(hashableId)) {
        addNodeMessages.push(
          this.addNodeWithoutCanvasUpdate(
            node,
            this.nonfetchedEdgeCounts.get(hashableId) ?? 0,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.lightHydrationData.get(hashableId),
            nodePositions[index],
          ),
        );
        this.nodeIdToCollapsedPairwiseNode.delete(hashableId);
        index++;
      }
    });

    for (const edge of this.edges.values()) {
      const sourceNodeId = nodeIdToHashable(edge.id1);
      const targetNodeId = nodeIdToHashable(edge.id2);
      // this works because all parsed nodes will only have two neighbors
      // and when its parsed/collapsed all its two edges will also be hidden
      if (
        (this.parsedNodesIdToNodes.has(sourceNodeId) && propertyNodesToAdd.has(sourceNodeId)) ||
        (this.parsedNodesIdToNodes.has(targetNodeId) && propertyNodesToAdd.has(targetNodeId))
      ) {
        edgesToAdd.push(edge);
      }
    }
    const addEdgesMessage = this.newAddEdgesMessage(edgesToAdd);
    const removeCollapseNodesMessage = this.newRemoveCollapsedPairwiseNodesMessage(collapsedNodes);

    const message = combineGraphUpdateMessages([
      ...addNodeMessages,
      addEdgesMessage,
      removeCollapseNodesMessage,
    ]);

    if (message) {
      this.canvasUpdateFn(message);
    }
    this.reactBroadcast();
    g6Wrapper.relayout();
  };

  private addCollapsedPairwiseNodes(g6Wrapper: G6Wrapper) {
    const removeNodeMessages: G6GraphUpdateMessage[] = [];
    const edgesToAdd: Edge[] = [];
    const nodesToAdd: CollapsedPairwiseNode[] = [];

    this.expandedCliqueIds.forEach((cliqueId) => {
      const clique = this.cliques.get(cliqueId);

      // initialize a collapsed pairwise node
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const newCollapsedNode = this.createCollapsedPairwiseNode(clique);
      nodesToAdd.push(newCollapsedNode);

      // initialize edges from the collapsed node to entity nodes of the clique
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      clique.entityNodes.forEach((node) => {
        edgesToAdd.push(this.createCollapsedPairwiseEdge(node, newCollapsedNode));
      });

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      clique.propertyNodes.forEach((hashableId) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.parsedNodesIdToNodes.set(hashableId, this.nodes.get(hashableId));
        removeNodeMessages.push(createG6RemoveNodeMessage(nodeIdFromHashable(hashableId)));
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.nodeIdToCollapsedPairwiseNode.set(hashableId, clique.id);
      });
    });
    const addCollapsedEdgesMessage = this.newAddCollapsedPairwiseEdgesMessage(edgesToAdd);
    const addCollapsedNodesMessage = this.newAddCollapsedPairwiseNodesMessage(nodesToAdd);

    const message = combineGraphUpdateMessages([
      ...removeNodeMessages,
      addCollapsedNodesMessage,
      addCollapsedEdgesMessage,
    ]);

    if (message) {
      this.canvasUpdateFn(message);
    }
    this.collapsedCliqueIds = new Set(this.cliques.keys());
    this.expandedCliqueIds.clear();

    this.reactBroadcast();
    g6Wrapper.relayout();
  }

  addCollapsedGroup(g6Wrapper: G6Wrapper) {
    this.collapsedGroupNonce++;

    const selectedNodeIds: Set<HashableNodeId> = new Set<HashableNodeId>();
    const selectedCollapsedGroupIds: Set<CollapsedGroupId> = new Set();
    // collapsedGroupMembers also includes all nodes within a selected Collapsed Group
    const collapsedGroupMembers: Set<HashableNodeId> = new Set<HashableNodeId>();

    const removeSelectedNodesMessages: G6GraphUpdateMessage[] = [];
    // nodes/collapsed groups to be collapsed
    for (const selectable of this.getSelectables()) {
      switch (selectable.type) {
        case RenderedCanvasNodeType.Node: {
          const nodeId = selectable.selectable;
          selectedNodeIds.add(nodeIdToHashable(nodeId));
          collapsedGroupMembers.add(nodeIdToHashable(nodeId));
          removeSelectedNodesMessages.push(createG6RemoveNodeMessage(nodeId));
          break;
        }
        case RenderedCanvasNodeType.CollapsedGroup: {
          const collapsedGroupId = selectable.selectable;
          selectedCollapsedGroupIds.add(collapsedGroupId);
          removeSelectedNodesMessages.push(createG6RemoveCollapsedGroupMessage(collapsedGroupId));
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          for (const member of this.collapsedGroups.get(collapsedGroupId).members) {
            collapsedGroupMembers.add(member);
          }
        }
      }
    }

    const relativeNodePositions = g6Wrapper.calculateRelativeNodePositions(
      [...selectedNodeIds].map(nodeIdFromHashable),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [...selectedCollapsedGroupIds].map((collapsedGroupId) =>
        this.collapsedGroups.get(collapsedGroupId),
      ),
    );

    // create collapsed group for canvas
    const newCollapsedGroup: CollapsedGroup = {
      groupId: this.collapsedGroupNonce.toString(),
      members: collapsedGroupMembers,
      state: defaultNodeState(),
      relativeNodePositions,
      hoverCards: new Set(),
    };
    const g6CollapsedGroup: G6CollapsedGroup = collapsedGroupToG6CollapsedGroup(newCollapsedGroup);
    const { x: newX, y: newY } = g6Wrapper.positionNewCollapsedGroup(
      selectedNodeIds,
      selectedCollapsedGroupIds,
    );
    g6CollapsedGroup.x = newX;
    g6CollapsedGroup.y = newY;

    const addCollapsedGroupMessage: G6GraphUpdateMessage = {
      items: [
        {
          messageType: 'add_collapsed_group',
          collapsedGroup: g6CollapsedGroup,
        },
      ],
    };

    // update graph-explorer state
    this.setSelectables([]);
    this.collapsedGroups.set(newCollapsedGroup.groupId, newCollapsedGroup);
    for (const groupId of selectedCollapsedGroupIds) {
      this.collapsedGroups.delete(groupId);
    }
    for (const nodeId of collapsedGroupMembers) {
      this.nodeIdToCollapsedGroup.set(nodeId, newCollapsedGroup.groupId);
    }

    // add new edges from neighboring nodes to new collapsed Group
    const edgesToAdd = [];
    for (const edge of this.edges.values()) {
      const sourceNodeId = nodeIdToHashable(edge.id1);
      const targetNodeId = nodeIdToHashable(edge.id2);
      // source is in collapsed group and target isnt
      if (collapsedGroupMembers.has(sourceNodeId) && !collapsedGroupMembers.has(targetNodeId)) {
        edgesToAdd.push(edge);
      }
      // source isnt in collapsed group and target is
      else if (
        !collapsedGroupMembers.has(sourceNodeId) &&
        collapsedGroupMembers.has(targetNodeId)
      ) {
        edgesToAdd.push(edge);
      }
    }
    const addEdgeMessages: G6GraphUpdateMessage = this.newAddEdgesMessage(edgesToAdd);

    const message: G6GraphUpdateMessage = combineGraphUpdateMessages([
      ...removeSelectedNodesMessages,
      addCollapsedGroupMessage,
      addEdgeMessages,
    ]);

    this.canvasUpdateFn(message);
    this.reactBroadcast();
  }

  getCollapsedGroupNodeBelongsTo = (nodeId: NodeId): CollapsedGroup | null => {
    const collapsedGroupId = this.nodeIdToCollapsedGroup.get(nodeIdToHashable(nodeId));
    if (collapsedGroupId) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return this.collapsedGroups.get(collapsedGroupId);
    }
    return null;
  };

  getCollapsedPairwiseNodeNodeBelongsTo = (nodeId: NodeId): CollapsedPairwiseNode | null => {
    const collapsedNodeId = this.nodeIdToCollapsedPairwiseNode.get(nodeIdToHashable(nodeId));
    if (collapsedNodeId) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return this.collapsedPairwiseNodes.get(collapsedNodeId);
    }
    return null;
  };

  // This function can be generalized to generate a subgraph of graph explorer
  // We can leave that until we have a second use case for grabbing a subgraph
  exportCollapsedGroupPreviewData = (collapsedGroup: CollapsedGroup): CollapsedGroupPreviewData => {
    const nodeIds = [...collapsedGroup.members];
    const nodes = nodeIds.map((nodeId) => this.nodes.get(nodeId));
    const nodeIdsSet = collapsedGroup.members;
    const edges = [];
    for (const e of this.edges.values()) {
      if (nodeIdsSet.has(nodeIdToHashable(e.id1)) && nodeIdsSet.has(nodeIdToHashable(e.id2))) {
        edges.push(e);
      }
    }
    const nonfetchedEdgeCounts = new Map();
    for (const nodeId of nodeIds) {
      nonfetchedEdgeCounts.set(nodeId, this.nonfetchedEdgeCounts.get(nodeId));
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const seedNodes = nodes.filter((node) => this.seedNodes.has(nodeIdToHashable(node.id)));

    const nodePositions: { [nodeId: HashableNodeId]: NodePosition } = {};
    for (const [nodeId, nodePosition] of collapsedGroup.relativeNodePositions) {
      nodePositions[nodeId] = nodePosition;
    }

    const lightHydrationData: { [nodeId: HashableNodeId]: LightlyHydratedNode } = {};
    for (const nodeId of nodeIds) {
      if (this.lightHydrationData.has(nodeId)) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lightHydrationData[nodeId] = this.lightHydrationData.get(nodeId);
      }
    }

    const v2Data = {
      nodes,
      edges,
      nonfetchedEdgeCounts,
      seedNodes,
    };

    return {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      v2Data,
      nodePositions,
      lightHydrationData,
      viewType: this.selectedViewType,
    };
  };

  // ---------------------------------------------------------------------------------------
  // Demo HAX
  // ---------------------------------------------------------------------------------------

  demoOnlyAddTags = (nodeIds: NodeId[], tags: string[]) => {
    const updateMessages: G6GraphUpdateMessage[] = [];

    nodeIds.forEach((nodeId) => {
      const hashableNodeId = nodeIdToHashable(nodeId);
      tags.forEach((tag) => {
        // Update the hydrated node, if it exists.
        const hydratedNode = this.hydrationData.get(hashableNodeId);
        if (
          hydratedNode &&
          hydratedNode.nodeType === 'account' &&
          !hydratedNode.hydrationData.tags.includes(tag)
        ) {
          hydratedNode.hydrationData.tags.push(tag);
        }

        // Update the light hydration data too.
        const lightlyHydratedNode = this.lightHydrationData.get(hashableNodeId);
        if (!lightlyHydratedNode) {
          // The lightly hydrated node should exist.
          throw new Error('Missing light hydration data');
        }
        if (
          lightlyHydratedNode.nodeType === 'account' &&
          !lightlyHydratedNode.hydrationData.tags.includes(tag)
        ) {
          lightlyHydratedNode.hydrationData.tags.push(tag);
        }
      });

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      updateMessages.push(this.newUpdateNodeMessage(this.nodes.get(hashableNodeId)));
    });

    // Update the canvas accordingly.
    this.canvasUpdateFn(combineGraphUpdateMessages(updateMessages));
  };

  // ---------------------------------------------------------------------------------------
  // Transactions Demo logic
  // ---------------------------------------------------------------------------------------

  private normalEdgeOrTxnEdgeToG6Edge(
    edge: Edge,
    lightHydrationData: Map<HashableNodeId, LightlyHydratedNode>,
  ): G6Edge | null {
    const hashableNodeId1 = nodeIdToHashable(edge.id1);
    const hashableNodeId2 = nodeIdToHashable(edge.id2);
    const node1 = this.nodes.get(hashableNodeId1);
    const node2 = this.nodes.get(hashableNodeId2);

    if (
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      node1.id.token === TRANSACTIONS_DATUM_TYPE.token ||
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      node2.id.token === TRANSACTIONS_DATUM_TYPE.token
    ) {
      // Transactions edge.
      let accountNodeState: NodeState;
      let transactionsNodeState: NodeState;
      let accountNodeAndLightHydration: NodeIdAndLightHydration;
      let transactionsNodeAndLightHydration: NodeIdAndLightHydration;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (node1.id.token === TRANSACTIONS_DATUM_TYPE.token) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        accountNodeState = node2.state;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        transactionsNodeState = node1.state;
        accountNodeAndLightHydration = {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          nodeId: node2.id,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lightHydration: lightHydrationData.get(hashableNodeId2),
        };
        transactionsNodeAndLightHydration = {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          nodeId: node1.id,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lightHydration: lightHydrationData.get(hashableNodeId1),
        };
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      } else if (node2.id.token === TRANSACTIONS_DATUM_TYPE.token) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        accountNodeState = node1.state;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        transactionsNodeState = node2.state;
        accountNodeAndLightHydration = {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          nodeId: node1.id,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lightHydration: lightHydrationData.get(hashableNodeId1),
        };
        transactionsNodeAndLightHydration = {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          nodeId: node2.id,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lightHydration: lightHydrationData.get(hashableNodeId2),
        };
      } else {
        throw new Error('assertion error for setData');
      }
      return transactionEdgeToG6Edge(
        accountNodeState,
        transactionsNodeState,
        accountNodeAndLightHydration,
        transactionsNodeAndLightHydration,
      );
    } else {
      // Normal edge.
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return edgeToG6Edge(edge, node1.state, node2.state);
    }
  }

  private collapsedPairwiseEdgeToG6Edge(edge: Edge): G6Edge | null {
    const node1 = this.nodes.get(nodeIdToHashable(edge.id1));
    const node2 = this.collapsedPairwiseNodes.get(edge.id2.value);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return collapsedPairwiseEdgeToG6EdgeWithState(edge, node1.state, node2.state);
  }

  // For usage with React's useSyncExternalStore
  reactSubscribe = (onStoreChange: () => void) => {
    this.reactSubscribers.add(onStoreChange);
    return () => this.reactSubscribers.delete(onStoreChange);
  };

  // For usage with React's useSyncExternalStore
  getSnapshot = () => {
    const hoveredNodeId = this.getHoveredNodeId();
    const hoveredCollapsedGroupId = this.getHoveredCollapsedGroupId();
    const hoveredCollapsedNodeId = this.getHoveredCollapsedPairwiseNodeId();
    const selectedNodes = this.getSelectedNodes();
    const uncollapsedNodeIds = this.getUncollapsedNodeIds();

    return {
      hoverCardX: this.getHoverCardX(),
      hoverCardY: this.getHoverCardY(),
      hoveredNode: hoveredNodeId ? this.getNodeById(nodeIdToHashable(hoveredNodeId)) : null,
      hydratedNode: hoveredNodeId != null ? this.getHydrationData(hoveredNodeId) : null,
      hoveredCollapsedGroup: hoveredCollapsedGroupId
        ? this.getCollapsedGroupById(hoveredCollapsedGroupId)
        : null,
      hoveredCollapsedPairwiseNode: hoveredCollapsedNodeId
        ? this.getCollapsedPairwiseNodeById(hoveredCollapsedNodeId)
        : null,
      hydratedCollapsedNodes:
        hoveredCollapsedNodeId != null
          ? this.getPairwiseMemberNodeIds(hoveredCollapsedNodeId).map(this.getHydrationData)
          : null,
      selectedNodes,
      uncollapsedNodeIds,
      exportNodes: (selectedNodes.length === 0 ? uncollapsedNodeIds : selectedNodes).filter(
        (node) => node.token === ACCOUNT_DATUM_TYPE.token,
      ),
      zoomPercentage: this.getZoomPercentage(),
      canZoomIn: this.canZoomIn(),
      canZoomOut: this.canZoomOut(),
      seedNodes: Array.from(this.seedNodes).map(nodeIdFromHashable),
      excludedNodes: Array.from(this.excludedNodes).map(nodeIdFromHashable),
      hiddenNodes: Array.from(this.hiddenNodes).map(nodeIdFromHashable),
      visibleNodes: Array.from(this.visibleNodes).map(nodeIdFromHashable),
      excludedNodesVisible: this.excludedNodesVisible,
      selectedViewType: this.getParseView(),
    };
  };

  reactBroadcast = () => {
    for (const subscriber of this.reactSubscribers) {
      subscriber();
    }
  };

  // For adding nodes' neighbors into a mapping
  private addNodeNeighbors = (
    node: HashableNodeId,
    neighbor: HashableNodeId,
    similarityNodeValue: HashableNodeId,
    nodeNeighbors: Map<HashableNodeId, Set<HashableNodeId>>,
    edgeToPropertyNode: Map<string, HashableNodeId>,
  ) => {
    const neighbors = (nodeNeighbors.get(node) || new Set()).add(neighbor);
    nodeNeighbors.set(node, neighbors);
    edgeToPropertyNode.set(`${node}-${neighbor}`, similarityNodeValue);
  };

  // Return a list of cliques (contains porperty nodes and edges)
  private getCliques = () => {
    // final result - largest cliques in current graph
    const largestCliques = new Map<CollapsedPairwiseNodeId, Clique>();
    // Maps account nodes' 2-step neighbors (ignores the connecting property node)
    const nodeNeighbors = new Map<HashableNodeId, Set<HashableNodeId>>();
    // Maps account node neighbors to the property node connecting them
    const edgeToPropertyNode = new Map<string, HashableNodeId>();
    const identifiedCliques = new Set<string>();
    // Note: the first item is a collection of property node Ids within a clique
    const cliques: [HashableNodeId[], HashableNodeId[]][] = [];

    // finds all cliques of size 2 (ie. an edge of 2 nodes)
    this.adjacencyLists.forEach((adjacencySet, similarityNodeValue) => {
      if (
        nodeIdFromHashable(similarityNodeValue).token === ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token
      ) {
        cliques.push([[similarityNodeValue], Array.from(adjacencySet)]);
      }
    });

    cliques.forEach(([similarityNodeValue, [node1, node2]]) => {
      this.addNodeNeighbors(
        node1,
        node2,
        similarityNodeValue[0],
        nodeNeighbors,
        edgeToPropertyNode,
      );
      this.addNodeNeighbors(
        node2,
        node1,
        similarityNodeValue[0],
        nodeNeighbors,
        edgeToPropertyNode,
      );
    });

    // iterate until there are no more cliques than can be expanded
    while (cliques.length) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const [similarityNodes, entityNodes] = cliques.pop();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const firstCommonNeighbor: HashableNodeId = this.findFirstCommonNeighbor(
        entityNodes,
        nodeNeighbors,
      );

      if (firstCommonNeighbor) {
        // k + 1 sized clique is now formed. Store the new property nodes and account node
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const newSimilarityNodes = entityNodes.map((node) =>
          edgeToPropertyNode.get(`${node}-${firstCommonNeighbor}`),
        );
        entityNodes.push(firstCommonNeighbor);
        similarityNodes.push(...newSimilarityNodes);
        cliques.push([similarityNodes, entityNodes]);
      } else {
        // prevents adding different ordering of the same clique
        const sortedNodes = entityNodes.sort().join('|');
        if (entityNodes.length > 2 && !identifiedCliques.has(sortedNodes)) {
          this.collapsedPairwiseNodeNonce++;

          const newClique = {
            id: this.collapsedPairwiseNodeNonce.toString(),
            entityNodes: new Set(entityNodes),
            propertyNodes: new Set(similarityNodes),
          };
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          largestCliques.set(newClique.id, newClique);
          identifiedCliques.add(sortedNodes);
        }
      }
    }

    this.cliques = largestCliques;
    if (this.selectedViewType === GraphViewTypes.Parsed) {
      this.collapsedCliqueIds = new Set(largestCliques.keys());
    } else {
      this.expandedCliqueIds = new Set(largestCliques.keys());
    }
  };

  // Returns the first common neighbor between a list of neighbors of nodes in an exisiting clique
  private findFirstCommonNeighbor = (
    accountNodes: HashableNodeId[],
    nodeNeighbors: Map<HashableNodeId, Set<HashableNodeId>>,
  ): HashableNodeId | undefined => {
    const neighborSets = accountNodes.map((node: HashableNodeId) => nodeNeighbors.get(node));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    let prevIntersections: HashableNodeId[] = Array.from(neighborSets[0]);

    // performs set intersection
    neighborSets.forEach((neighbors) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      prevIntersections = prevIntersections.filter((node: HashableNodeId) => neighbors.has(node));
    });
    return prevIntersections[0];
  };
}
